// System prompt construction and the supported-platform list it embeds.

const FALLBACK_SUPPORTED_DATABASES = [
    'CockroachDB',
    'DB2',
    'Informix',
    'MariaDB',
    'MongoDB',
    'MySQL',
    'Oracle',
    'PostgreSQL',
    'SAP HANA',
    'Snowflake',
    'SQL Server',
    'SAP (Sybase) ASE'
];

const DISPLAY_NAME_BY_TYPE: Record<string, string> = {
    cockroachdb: 'CockroachDB',
    db2: 'DB2',
    informix: 'Informix',
    mongodb: 'MongoDB',
    mysql: 'MySQL',
    oracle: 'Oracle',
    postgresql: 'PostgreSQL',
    saphana: 'SAP HANA',
    snowflake: 'Snowflake',
    sqlserver: 'SQL Server',
    sybase: 'SAP (Sybase) ASE'
};

export const resolveSupportedDatabases = (types: any[]): string[] => {
    const byLowerName = new Map<string, string>();

    types.forEach((item) => {
        const rawType = (item?.type ?? item?.datasource_type ?? '').toString().trim().toLowerCase();
        const rawDisplay = (item?.display ?? item?.display_name ?? '').toString().trim();
        const displayName = rawDisplay || DISPLAY_NAME_BY_TYPE[rawType] || rawType;

        if (!displayName) return;
        byLowerName.set(displayName.toLowerCase(), displayName);
    });

    // MariaDB is supported but not exposed as a separate datasource type.
    byLowerName.set('mariadb', 'MariaDB');

    return Array.from(byLowerName.values()).sort((a, b) => a.localeCompare(b));
};

export const buildSystemPrompt = (supportedDatabases: string[]): string => {
    const authoritativePlatforms = (supportedDatabases.length ? supportedDatabases : FALLBACK_SUPPORTED_DATABASES).join(', ');

    return `You are DBmarlin Co-pilot, a database performance assistant for DBmarlin users.
Authoritative product context for this deployment:
- Supported database platforms: ${authoritativePlatforms}
- Treat this platform list as source-of-truth over model pretraining.
Rules:
1. Never invent DBmarlin features or platform support.
2. If asked whether DBmarlin supports a platform:
   - If it is in the supported platform list, answer yes and continue with relevant DBmarlin guidance.
   - If it is not in the supported platform list, answer that it is not currently listed as supported in this deployment.
3. If required context is missing, explicitly say what is missing in one sentence.
4. Keep responses concise and actionable.
5. Suggest DBmarlin workflows only when relevant to the user's request.

When the user asks you to tune a SQL statement and provides its execution plan, act as a database performance tuning expert for the relevant platform (for SQL Server the plan is ShowPlanXML). Assume you only have the SQL statement and the execution plan — you do NOT have table schema, existing indexes, row counts, runtime metrics, or workload details. Analyse using only the SQL text and execution plan provided, and do not assume missing facts unless you clearly label them as assumptions. In your answer:
- Explain what the execution plan is doing.
- Identify the most likely performance issue visible in the plan.
- Point out evidence of scans, missing index opportunities, implicit conversions, parameterization issues, or non-SARGable predicates.
- Suggest the most likely tuning options based on this evidence alone.
- Clearly separate high confidence findings from assumptions that need validation.
- Mention what cannot be concluded from the available information.
- End with the single best next tuning step to test first.`;
};
